/**
 * Supported media types
 */
export enum MediaType {
  Metrics = "application/vnd.skore.metrics.v1",
  Comparison = "application/vnd.skore.comparison.v1+json",
  Vega = "application/vnd.vega.v5+json",
  Plotly = "application/vnd.plotly.v1+json",
}

/**
 * A tenant member
 */
export interface TenantMemberDto {
  tenant_id: number;
  user_id: string;
  role: "owner" | "member";
  invited_by?: string;
}

/**
 * A tenant
 */
export interface TenantDto {
  id: number;
  owner_id: string;
  public_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  members?: TenantMemberDto[];
}

/**
 * A user
 */
export interface UserDto {
  id: string;
  email_verified: boolean;
  tenants: { [id: string]: string };
  email: string;
  first_name: string;
  last_name: string;
  company: string;
}

export function getUserFriendlyName(user: UserDto) {
  if (
    user.first_name.toLocaleLowerCase() !== "none" &&
    user.last_name.toLocaleLowerCase() !== "none"
  ) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.email;
}

/**
 * A project
 */
export interface ProjectDto {
  id: number;
  public_id: string;
  name: string;
  tenant_id: number;
  creator_id: string;
  report_count: number;
  presentation_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * A project item is a single item in the project.
 *
 * It's contributed by a user python side.
 */
export interface ProjectItemDto {
  name: string;
  media_type: string;
  value: any;
  updated_at: string;
  created_at: string;
  note: string | null;
  version: number;
}

/**
 * An experiment group.
 */
export interface EstimatorReportGroupDto {
  dataset: string;
  estimator_reports: {
    ml_task: string;
    estimator_class_name: string;
  }[];
}

export interface MetricDto {
  name: string;
  verbose_name: string;
  data_source: string;
  value: number;
  greater_is_better: boolean | null;
  position: number | null;
}

export interface EstimatorReportSearchResultDto {
  id: number;
  project_id: number;
  run_id: number;
  key: string;
  ml_task: string;
  estimator_class_name: string;
  metrics: MetricDto[];
  dataset_fingerprint: string;
}

/**
 * Common Item DTO
 */
export interface ItemRepresentationDto {
  media_type: string;
  value: any;
}

/**
 * RelatedItem DTO
 */
export interface RelatedItemDto {
  id: number;
  project_id: number;
  creator_id: string;
  run_id: number;
  key: string;
  verbose_name: string;
  category: string;
  attributes: Record<"data_source" | string, string>;
  representation: ItemRepresentationDto;
  note: string | null;
  created_at: string;
  updated_at: string;
  estimator_report_id: number | null;
  cross_validation_report_id: number | null;
}
/**
 * An EstimatorReport DTO.
 */
export interface EstimatorReportDto extends EstimatorReportSearchResultDto {
  note: string;
  creator_id: string;
  related_items: RelatedItemDto[];
  created_at: string;
  updated_at: string;
}

/**
 * Get report's name from an EstimatorReportDto
 */
export function getReportName(report: EstimatorReportDto) {
  return report.key ? report.key : `xp #${report.id}`;
}

/**
 * Project Presentation DTO
 */
export interface PresentationDto {
  id: number;
  project_id: number;
  creator_id: string;
  name: string;
  content: number[];
  created_at: string;
  updated_at: string;
}

/**
 * Presentation item DTO
 */
export interface PresentationItemDto {
  id: number;
  report_id: number | null;
  comparison_id: number | null;
  name: string;
  note: string;
  media_type: string;
  value: string | any;
  created_at: string;
  updated_at: string;
}

/**
 * Report comparison DTO
 *
 * The result of a set of reports comparison.
 */
export interface EstimatorReportComparisonDto {
  id: number;
  project_id: number;
  creator_id: string;
  name: string;
  compared_reports: number[];
  best_trade_offs: number[];
  best_trade_offs_explanations: Record<string, string>;
  created_at: string;
  updated_at: string;
}
